class IaBundleComponent extends HTMLElement {
  connectedCallback() {
    this.requiredItems = Number(this.dataset.requiredItems || 0);
    this.addText = this.dataset.addText || "Add";
    this.addedText = this.dataset.addedText || "Added";
    this.selectText = this.dataset.selectText || "Select product";
    this.buttonSelectTemplate = this.dataset.buttonSelectTemplate || "Select [n] more product";
    this.addToCartText = this.dataset.addToCartText || "Add to cart";
    this.cardAddText = this.dataset.cardAddText || "Add to bundle";
    this.cardAddedText = this.dataset.cardAddedText || "Added";
    this.cardSoldOutText = this.dataset.cardSoldoutText || "Out of Stock";
    const rawDiscount = Number(this.dataset.bundleDiscountPercent);
    this.bundleDiscountPercent = Number.isFinite(rawDiscount)
      ? Math.min(100, Math.max(0, rawDiscount))
      : 0;

    this.tabs = Array.from(this.querySelectorAll("[data-ia-bundle-tab]"));
    this.panels = Array.from(this.querySelectorAll("[data-ia-bundle-panel]"));
    this.cards = Array.from(this.querySelectorAll("[data-ia-bundle-card]"));
    this.sidebar = this.querySelector("[data-ia-bundle-sidebar]");
    this.sidebarSlots = Array.from(this.querySelectorAll("[data-slot-index]"));
    this.countEl = this.querySelector("[data-ia-bundle-count]");
    this.subtotalEl = this.querySelector("[data-ia-bundle-subtotal]");
    this.submitButton = this.querySelector("[data-ia-bundle-submit]");
    this.cart = document.querySelector("ia-cart") || document.querySelector("ia-mini-cart");

    this.selectedItems = [];

    this.bindTabs();
    this.bindProducts();
    this.bindSidebar();
    this.bindPagination();
    this.bindSubmit();
    this.render();
  }

  syncCards() {
    this.cards = Array.from(this.querySelectorAll("[data-ia-bundle-card]"));
  }

  bindTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        const index = Number(tab.dataset.index);
        this.tabs.forEach((node, nodeIndex) => {
          const active = nodeIndex === index;
          node.classList.toggle("is-active", active);
          node.setAttribute("aria-selected", String(active));
        });

        this.panels.forEach((panel, panelIndex) => {
          const active = panelIndex === index;
          panel.classList.toggle("is-active", active);
          panel.hidden = !active;
        });
      });
    });
  }

  bindProducts() {
    this.addEventListener("ia-bundle-product:select", (event) => {
      const detail = event.detail || {};
      this.toggleItem(detail);
    });
  }

  formatPrice(cents) {
    const dollars = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: Shopify.currency.active,
    })
      .format(dollars)
      .replace(/\.00$/, "");
  }

  toggleItem(itemData) {
    const variantId = itemData.variantId;
    if (!variantId) return;

    const existingIndex = this.selectedItems.findIndex((item) => item.variantId === variantId);
    if (existingIndex > -1) {
      this.selectedItems.splice(existingIndex, 1);
      this.render();
      return;
    }

    if (this.selectedItems.length >= this.requiredItems) {
      return;
    }

    this.selectedItems.push({
      variantId,
      title: itemData.title || "",
      description: itemData.description || "",
      priceCents: Number(itemData.priceCents || 0),
      imageSrc: itemData.imageSrc || "",
      imageAlt: itemData.imageAlt || "",
    });
    this.render();
  }

  bindSidebar() {
    if (!this.sidebar) return;
    this.sidebar.addEventListener("click", (event) => {
      const removeButton = event.target.closest("[data-ia-bundle-remove]");
      if (!removeButton) return;
      const slot = removeButton.closest("[data-slot-index]");
      if (!slot) return;
      const slotIndex = Number(slot.dataset.slotIndex);
      if (!this.selectedItems[slotIndex]) return;
      this.selectedItems.splice(slotIndex, 1);
      this.render();
    });
  }

  bindPagination() {
    this.addEventListener("ia-pagination:items-appended", () => {
      this.syncCards();
      this.render();
    });
  }

  bindSubmit() {
    if (!this.submitButton) return;
    this.submitButton.addEventListener("click", async () => {
      if (this.selectedItems.length !== this.requiredItems) return;
      const ts = window.iaCartAPI?.getTimestamp();
      const items = this.selectedItems.map((item) => ({
        id: Number(item.variantId),
        properties: {
          "_is_bundle_item": "true",
          "_bundle_id": ts,
        },
        quantity: 1,
      }));

      try {
        this.submitButton.disabled = true;
        const response = await window.iaCartAPI?.addToCartJson({ items });
        this.cart?.open(response);
      } catch (error) {
        console.error("[ia-bundle] Add to cart failed", error);
      } finally {
        this.submitButton.disabled = this.selectedItems.length !== this.requiredItems;
      }
    });
  }

  sidebarBlankTemplate(slotIndex) {
    return `
      <div class="ia-bundle__sidebar-item" data-slot-index="${slotIndex}">
        <div class="ia-bundle__sidebar-item-main">
          <div class="ia-bundle__sidebar-item-image"></div>
          <div class="ia-bundle__sidebar-item-content">
            <div class="ia-bundle__sidebar-item-text">
              <div class="ia-bundle__sidebar-item-title">${this.selectText}</div>
              <div class="ia-bundle__sidebar-item-subtitle" data-ia-bundle-item-description></div>
            </div>
            <div class="ia-bundle__sidebar-item-price" data-ia-bundle-item-price></div>
          </div>
        </div>
        <button type="button" class="ia-bundle__remove ia-iconic-button" data-ia-bundle-remove hidden aria-label="Remove item">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="M15 9L9 15M9 9L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
        </button>
      </div>
    `;
  }

  sidebarFilledTemplate(item, slotIndex) {
    const image = item.imageSrc ? `<img src="${item.imageSrc}" alt="${item.imageAlt || item.title}">` : "";
    const description = item.description || "";
    const price = this.formatPrice(item.priceCents || 0);

    return `
      <div class="ia-bundle__sidebar-item is-active" data-slot-index="${slotIndex}">
        <div class="ia-bundle__sidebar-item-main">
          <div class="ia-bundle__sidebar-item-image">${image}</div>
          <div class="ia-bundle__sidebar-item-content">
            <div class="ia-bundle__sidebar-item-text">
              <div class="ia-bundle__sidebar-item-title">${item.title}</div>
              <div class="ia-bundle__sidebar-item-subtitle" data-ia-bundle-item-description>${description}</div>
            </div>
            <div class="ia-bundle__sidebar-item-price" data-ia-bundle-item-price>${price}</div>
          </div>
        </div>
        <button type="button" class="ia-bundle__remove ia-iconic-button" data-ia-bundle-remove aria-label="Remove item">
          <svg class="ia-icon ia-icon--close" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.22578 18.8258L5.17578 17.7758L10.9508 12.0008L5.17578 6.22578L6.22578 5.17578L12.0008 10.9508L17.7758 5.17578L18.8258 6.22578L13.0508 12.0008L18.8258 17.7758L17.7758 18.8258L12.0008 13.0508L6.22578 18.8258Z" fill="currentColor"/>
          </svg>
        </button>
      </div>
    `;
  }

  render() {
    const selectedCount = this.selectedItems.length;
    const remainingCount = Math.max(this.requiredItems - selectedCount, 0);
    const isComplete = selectedCount === this.requiredItems;

    if (this.countEl) this.countEl.textContent = String(this.selectedItems.length);

    const subtotal = this.selectedItems.reduce((sum, item) => sum + Number(item.priceCents || 0), 0);
    if (this.subtotalEl) {
      if (subtotal <= 0) {
        this.subtotalEl.textContent = "";
        this.subtotalEl.innerHTML = "";
      } else if (this.bundleDiscountPercent > 0 && isComplete) {
        const saleCents = Math.round((subtotal * (100 - this.bundleDiscountPercent)) / 100);
        const compare = this.formatPrice(subtotal);
        const sale = this.formatPrice(saleCents);
        this.subtotalEl.innerHTML = `<span class="ia-bundle__sidebar-subtotal-compare"><s>${compare}</s></span><span class="ia-bundle__sidebar-subtotal-sale">${sale}</span>`;
      } else {
        this.subtotalEl.innerHTML = "";
        this.subtotalEl.textContent = this.formatPrice(subtotal);
      }
    }
    if (this.submitButton) {
      this.submitButton.classList.toggle("is-active", isComplete);
      this.submitButton.classList.toggle("ia-button--primary", isComplete);
      this.submitButton.classList.toggle("ia-button--outline", !isComplete);
      const remainingText = this.buttonSelectTemplate
        .replace("[n]", String(remainingCount))
        .replace(/\bproducts\b/i, remainingCount === 1 ? "product" : "products")
        .replace(/\bproduct\b/i, remainingCount === 1 ? "product" : "products");
      this.submitButton.textContent = isComplete
        ? this.addToCartText
        : remainingText;
    }

    const sidebarRoot = this.sidebar ? this.sidebar.closest(".ia-bundle__sidebar") : null;
    if (sidebarRoot) {
      sidebarRoot.classList.toggle("is-active", selectedCount > 0);
    }

    this.cards.forEach((card) => {
      const selected = this.selectedItems.some((item) => item.variantId === card.dataset.variantId);
      card.classList.toggle("is-selected", selected);
      const addBtn = card.querySelector("[data-ia-bundle-card-add]");
      if (addBtn && !addBtn.hasAttribute("data-ia-bundle-card-soldout")) {
        addBtn.textContent = selected ? this.cardAddedText : this.cardAddText;
        addBtn.setAttribute("aria-pressed", selected ? "true" : "false");
        addBtn.classList.toggle("ia-button--primary", !selected);
        addBtn.classList.toggle("ia-button--outline", selected);
      }
    });

    if (this.sidebar) {
      const slots = Array.from({ length: this.requiredItems }, (_, index) => {
        const item = this.selectedItems[index];
        return item ? this.sidebarFilledTemplate(item, index) : this.sidebarBlankTemplate(index);
      });
      this.sidebar.innerHTML = slots.join("");
    }
  }
}

class IaBundleProduct extends HTMLElement {
  connectedCallback() {
    this.isAvailable = this.dataset.variantAvailable !== "false";
    this.addEventListener("click", this.onClick);
    this.mountBundleButton();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    this.addButton?.removeEventListener("click", this.onAddButtonClick);
    this._bundleBuyHost?.remove();
    this._bundleBuyHost = null;
    this.addButton = null;
  }

  /**
   * Same host as add-to-cart: `.ia-product-card__buy` at end of `.ia-product-card__content`.
   */
  mountBundleButton(attempt = 0) {
    if (this.querySelector("[data-ia-bundle-card-add]")) return;

    const content = this.querySelector("ia-product-card .ia-product-card__content");
    if (!content) {
      if (attempt < 8) {
        requestAnimationFrame(() => this.mountBundleButton(attempt + 1));
      }
      return;
    }

    const root = this.closest("ia-bundle-component");
    const addText = root?.dataset.cardAddText || "Add to bundle";
    const soldOutText = root?.dataset.cardSoldoutText || "Out of Stock";

    const buy = document.createElement("div");
    buy.className = "ia-product-card__buy";

    const btn = document.createElement("button");
    btn.type = "button";
    btn.setAttribute("data-ia-bundle-card-add", "");

    if (this.isAvailable) {
      btn.className = "ia-button ia-button--primary ia-button--full-width";
      btn.textContent = addText;
      btn.setAttribute("aria-pressed", "false");
      btn.addEventListener("click", this.onAddButtonClick);
    } else {
      btn.className = "ia-button ia-button--outline ia-button--full-width";
      btn.textContent = soldOutText;
      btn.disabled = true;
      btn.setAttribute("aria-disabled", "true");
      btn.setAttribute("data-ia-bundle-card-soldout", "");
    }

    buy.appendChild(btn);
    content.appendChild(buy);

    this._bundleBuyHost = buy;
    this.addButton = btn;
  }

  getSelectDetail() {
    return {
      variantId: this.dataset.variantId,
      title: this.dataset.productTitle || "",
      description: this.dataset.productDescription || "",
      priceCents: this.dataset.variantPrice || "0",
      imageSrc: this.dataset.imageSrc || "",
      imageAlt: this.dataset.imageAlt || "",
    };
  }

  dispatchSelect() {
    this.dispatchEvent(
      new CustomEvent("ia-bundle-product:select", {
        bubbles: true,
        detail: this.getSelectDetail(),
      })
    );
  }

  onAddButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isAvailable) return;
    this.dispatchSelect();
  };

  onClick = (event) => {
    const link = event.target.closest("a");
    if (link) {
      event.preventDefault();
    }

    if (event.target.closest("button, input, select, textarea, label")) {
      return;
    }

    if (!this.isAvailable) return;

    this.dispatchSelect();
  };
}

customElements.define("ia-bundle-component", IaBundleComponent);
customElements.define("ia-bundle-product", IaBundleProduct);
